(() => {
  const roots = document.querySelectorAll('[data-twbc-watch-checker]');
  if (!roots.length) return;

  roots.forEach((root) => {
    if (root.dataset.twbcInitialized === 'true') return;
    root.dataset.twbcInitialized = 'true';

    const overlay = root.querySelector('[data-twbc-overlay]');
    const drawers = [...root.querySelectorAll('[data-twbc-drawer]')];
    const openButtons = [...root.querySelectorAll('[data-twbc-open]')];
    const closeButtons = [...root.querySelectorAll('[data-twbc-close]')];
    const searchInput = root.querySelector('[data-twbc-search]');
    const resultsEl = root.querySelector('[data-twbc-results]');
    const answerEl = root.querySelector('[data-twbc-answer]');
    const emptyEl = root.querySelector('[data-twbc-empty]');
    const loadingEl = root.querySelector('[data-twbc-loading]');
    const dataUrl = root.dataset.watchDataUrl;

    if (!overlay || !drawers.length) return;

    let watchData = [];
    let lastFocus = null;

    /*
      IMPORTANT:
      Move overlay + drawers directly under <body>
      so Atelier / GemPages cannot trap them inside
      a stacking context.
    */
    document.body.appendChild(overlay);

    drawers.forEach((drawer) => {
      document.body.appendChild(drawer);
    });

    const normalize = (value = '') =>
      String(value)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

    const movementLabel = (movement) => {
      const m = normalize(movement);

      if (m === 'automatic') return 'Automatic';
      if (m === 'quartz') return 'Quartz / battery-powered';
      if (m === 'manual') return 'Manual-wind mechanical';

      return 'Movement unknown';
    };

    const getDrawer = (name) =>
      drawers.find(
        (drawer) => drawer.dataset.twbcDrawer === name
      );

    const openDrawer = (name) => {
      const drawer = getDrawer(name);
      if (!drawer) return;

      lastFocus = document.activeElement;

      overlay.hidden = false;

      requestAnimationFrame(() => {
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
      });

      document.body.classList.add(
        'twbc-watch-checker-is-open'
      );

      const focusTarget =
        name === 'mechanics'
          ? drawer.querySelector('[data-twbc-search]')
          : drawer.querySelector('[data-twbc-close]');

      setTimeout(() => {
        focusTarget?.focus();
      }, 120);
    };

    const closeAll = () => {
      drawers.forEach((drawer) => {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
      });

      overlay.hidden = true;

      document.body.classList.remove(
        'twbc-watch-checker-is-open'
      );

      if (
        lastFocus &&
        typeof lastFocus.focus === 'function'
      ) {
        lastFocus.focus();
      }
    };

    openButtons.forEach((button) => {
      button.addEventListener('click', () => {
        openDrawer(button.dataset.twbcOpen);
      });
    });

    closeButtons.forEach((button) => {
      button.addEventListener('click', closeAll);
    });

    overlay.addEventListener('click', closeAll);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        closeAll();
      }
    });

    const renderAnswer = (watch) => {
      if (
        !answerEl ||
        !emptyEl ||
        !resultsEl
      ) {
        return;
      }

      const brandModel = [
        watch.brand,
        watch.model
      ]
        .filter(Boolean)
        .join(' ');

      const reference = watch.reference
        ? ` (${watch.reference})`
        : '';

      const movement = normalize(
        watch.movement
      );

      answerEl.className =
        'twbc-watch-checker__answer';

      answerEl.hidden = false;
      emptyEl.hidden = true;
      resultsEl.hidden = true;

      if (movement === 'automatic') {
        answerEl.classList.add(
          'twbc-watch-checker__answer--yes'
        );

        answerEl.innerHTML = `
          <h3>
            <span class="twbc-watch-checker__tick">✓</span>
            Yes — this watch is automatic.
          </h3>

          <p>
            <strong>
              ${brandModel}${reference}
            </strong>
            uses an automatic movement.
          </p>

          <p>
            It will work with our automatic watch winders.
          </p>

          <p class="twbc-watch-checker__status">
            Mechanics: compatible
          </p>
        `;

        return;
      }

      if (movement === 'quartz') {
        answerEl.classList.add(
          'twbc-watch-checker__answer--no'
        );

        answerEl.innerHTML = `
          <h3>
            <span class="twbc-watch-checker__cross">×</span>
            This model is not automatic.
          </h3>

          <p>
            <strong>
              ${brandModel}${reference}
            </strong>
            is quartz / battery-powered.
          </p>

          <p>
            A watch winder will not keep this type
            of movement powered.
          </p>

          <p class="twbc-watch-checker__status">
            Mechanics: not applicable
          </p>
        `;

        return;
      }

      if (movement === 'manual') {
        answerEl.classList.add(
          'twbc-watch-checker__answer--no'
        );

        answerEl.innerHTML = `
          <h3>
            <span class="twbc-watch-checker__cross">×</span>
            This model is mechanical, but not automatic.
          </h3>

          <p>
            <strong>
              ${brandModel}${reference}
            </strong>
            is manually wound.
          </p>

          <p>
            A watch winder is designed for automatic
            movements, so it will not wind this watch.
          </p>

          <p class="twbc-watch-checker__status">
            Mechanics: not compatible for winding
          </p>
        `;

        return;
      }

      answerEl.classList.add(
        'twbc-watch-checker__answer--unknown'
      );

      answerEl.innerHTML = `
        <h3>
          <span class="twbc-watch-checker__warn">!</span>
          We can’t confirm this movement yet.
        </h3>

        <p>
          We have
          <strong>
            ${brandModel}${reference}
          </strong>
          in the database, but the movement type
          is not verified.
        </p>

        <p>
          Please check whether the watch is described
          as <strong>automatic</strong> before purchasing
          for winding use.
        </p>
      `;
    };

    const renderResults = (matches) => {
      if (
        !answerEl ||
        !emptyEl ||
        !resultsEl
      ) {
        return;
      }

      answerEl.hidden = true;

      if (!matches.length) {
        resultsEl.hidden = true;
        emptyEl.hidden = false;
        return;
      }

      emptyEl.hidden = true;
      resultsEl.hidden = false;
      resultsEl.innerHTML = '';

      matches
        .slice(0, 12)
        .forEach((watch) => {
          const button =
            document.createElement('button');

          button.type = 'button';
          button.className =
            'twbc-watch-checker__result-row';

          const title = [
            watch.brand,
            watch.model
          ]
            .filter(Boolean)
            .join(' ');

          const meta = [
            watch.reference
              ? `Ref. ${watch.reference}`
              : '',
            movementLabel(watch.movement)
          ]
            .filter(Boolean)
            .join(' · ');

          button.innerHTML = `
            <strong>${title}</strong>
            <span>${meta}</span>
          `;

          button.addEventListener(
            'click',
            () => renderAnswer(watch)
          );

          resultsEl.appendChild(button);
        });
    };

    const search = () => {
      if (
        !searchInput ||
        !answerEl ||
        !resultsEl ||
        !emptyEl
      ) {
        return;
      }

      const q = normalize(searchInput.value);

      answerEl.hidden = true;

      if (!q) {
        resultsEl.hidden = true;
        emptyEl.hidden = true;
        return;
      }

      const terms = q
        .split(/\s+/)
        .filter(Boolean);

      const matches = watchData.filter(
        (watch) => {
          const haystack = normalize(
            [
              watch.brand,
              watch.family,
              watch.model,
              watch.reference,
              ...(watch.aliases || [])
            ]
              .filter(Boolean)
              .join(' ')
          );

          return terms.every((term) =>
            haystack.includes(term)
          );
        }
      );

      renderResults(matches);
    };

    searchInput?.addEventListener(
      'input',
      search
    );

    if (dataUrl) {
      fetch(dataUrl, {
        credentials: 'same-origin'
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `Watch data failed to load: ${response.status}`
            );
          }

          return response.json();
        })
        .then((data) => {
          watchData = Array.isArray(data)
            ? data
            : [];

          if (loadingEl) {
            loadingEl.hidden = true;
          }
        })
        .catch((error) => {
          console.error(
            '[TWBC watch checker]',
            error
          );

          if (loadingEl) {
            loadingEl.textContent =
              'Watch database could not be loaded. Please try again later.';
          }
        });
    } else if (loadingEl) {
      loadingEl.textContent =
        'Watch database could not be loaded.';
    }
  });
})();